import { useState } from "react";
import { toast } from "sonner";
import { Clock, Radio } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { SectionHead } from "@/components/site/SectionHead";
import { useLang } from "@/context/LangContext";

function RegisterDialog({ webinar, onClose }) {
  const { t, item } = useLang();
  const [form, setForm] = useState({ name: "", phone: "" });
  const [title, , when] = webinar ? item(webinar.id, webinar.title, webinar.desc, webinar.when) : [];
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/webinars/register", { webinar_id: webinar.id, ...form });
      toast.success(t("web.done", { t: title }));
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!webinar} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-parchment max-w-md" data-testid="webinar-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-forest-800">{t("web.register")}</DialogTitle>
          <DialogDescription>{title} · {when}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5"><Label>{t("co.name")}</Label><Input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="webinar-name-input" /></div>
          <div className="space-y-1.5"><Label>{t("co.phone")}</Label><Input required inputMode="numeric" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="webinar-phone-input" /></div>
          <button disabled={busy} className="w-full bg-forest-800 text-parchment font-semibold py-3.5 rounded-full hover:bg-forest-700 disabled:opacity-60" data-testid="webinar-submit-button">{t("web.reserve")}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Webinars({ webinars }) {
  const [active, setActive] = useState(null);
  const { t, item } = useLang();
  return (
    <section id="webinars" className="relative bg-forest-800 text-parchment grain">
      <div className="relative max-w-6xl mx-auto px-5 py-20 sm:py-28 grid lg:grid-cols-[1fr_1.4fr] gap-12">
        <SectionHead light eyebrow={t("web.eyebrow")} title={t("web.title")} text={t("web.text")} />
        <div className="space-y-5">
          {webinars?.map((w) => {
            const [title, desc, when] = item(w.id, w.title, w.desc, w.when);
            return (
            <div key={w.id} className="border border-parchment/15 bg-forest-900/50 backdrop-blur rounded-2xl p-6 sm:p-7 hover:border-brass-500/60 transition-colors" data-testid={`webinar-card-${w.id}`}>
              <div className="flex flex-wrap items-center gap-3 text-xs text-brass-300">
                <span className="flex items-center gap-1.5 uppercase tracking-widest font-semibold"><Radio className="w-3.5 h-3.5" /> {t("web.live")}</span>
                <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {when} · {t("web.dur")}</span>
              </div>
              <h3 className="mt-3 text-2xl sm:text-3xl font-semibold">{title}</h3>
              <p className="mt-2 text-sm text-parchment/70 leading-relaxed">{desc}</p>
              <button onClick={() => setActive(w)} className="mt-5 px-6 py-2.5 rounded-full bg-brass-500 text-forest-900 font-semibold text-sm hover:bg-brass-300 transition-colors" data-testid={`webinar-register-${w.id}`}>
                {t("web.register")}
              </button>
            </div>
            );
          })}
        </div>
      </div>
      <RegisterDialog webinar={active} onClose={() => setActive(null)} />
    </section>
  );
}
